/**
 * Shared shell for the route-backed System & Betrieb sections.
 *
 * The route itself is protected by `system.admin` in App.tsx. More powerful
 * actions stay guarded inside their respective child pages (`scan.run`,
 * `schema.admin`, `import.write`). Health and the deployment-variant notice are
 * global context, so they remain visible while the operator moves between the
 * thematic sections rendered through `<Outlet/>`.
 */

import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { Banner } from '../components/Banner';
import { Chip } from '../components/Chip';
import PageHeader from '../components/PageHeader';
import Spinner from '../components/Spinner';
import { errorMessage, fetchHealth } from '../lib/api';
import { useAuth } from '../lib/auth';

import './admin/ops.css';

type HealthState = 'unknown' | 'ok' | 'error';

const SYSTEM_SECTIONS = [
  { to: '/admin/system', label: msg`Konfiguration & Status`, end: true },
  { to: '/admin/system/csaf', label: msg`CSAF-Quellen`, end: false },
  { to: '/admin/system/sbom', label: msg`SBOM-Import & Export`, end: false },
  { to: '/admin/system/versand', label: msg`Benachrichtigungsversand`, end: false },
  { to: '/admin/system/datenmodell', label: msg`Datenmodell & Demo`, end: false },
] as const;

export function SystemPage(): ReactNode {
  const { t, i18n } = useLingui();
  const { user } = useAuth();
  const [health, setHealth] = useState<HealthState>('unknown');
  const [healthDetail, setHealthDetail] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const loadHealth = useCallback(async (): Promise<void> => {
    setHealthLoading(true);
    try {
      const response = await fetchHealth();
      setHealth(response.status === 'ok' ? 'ok' : 'error');
      setHealthDetail(null);
    } catch (err) {
      setHealth('error');
      setHealthDetail(errorMessage(err));
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  return (
    <>
      <PageHeader
        title={t`System & Betrieb`}
        actions={
          <>
            <Chip
              tone={health === 'ok' ? 'good' : health === 'error' ? 'alert' : 'neutral'}
              title={healthDetail ?? undefined}
            >
              {healthLoading
                ? t`Health: prüfe…`
                : health === 'ok'
                  ? t`Health: ok`
                  : health === 'error'
                    ? t`Health: Fehler`
                    : t`Health: unbekannt`}
            </Chip>
            <button type="button" className="ghost" onClick={() => void loadHealth()} disabled={healthLoading}>
              {healthLoading ? <Spinner label={t`Prüft…`} /> : t`Health prüfen`}
            </button>
          </>
        }
      />

      {health === 'error' ? (
        <Banner kind="error" sub={healthDetail ?? undefined}>
          <Trans>
            Der Health-Check ist fehlgeschlagen. Wahrscheinlich startet der Server gerade neu oder der
            Proxy ist nicht erreichbar.
          </Trans>
        </Banner>
      ) : null}

      {user?.app_variant === 'demo' ? (
        <Banner kind="warn">
          <Trans>
            Demo-Modus: Änderungen wirken auf die lokalen Dienste. Den Ausgangszustand stellen
            Sie unter „Datenmodell &amp; Demo“ wieder her.
          </Trans>
        </Banner>
      ) : null}

      <nav className="tabs ops-system-nav" aria-label={t`Systembereiche`}>
        {SYSTEM_SECTIONS.map((section) => (
          <NavLink key={section.to} to={section.to} end={section.end}>
            {i18n._(section.label)}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </>
  );
}

export default SystemPage;
